import { Button, Tab, Tabs, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useContactAuth } from "hooks/useContactAuthContext";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import contactHttpClient from "utils/contactHttpClient";
import { useLogoForScheme } from "utils/themeContextHelper";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import Box from "@mui/material/Box";
import { AppBar, AppBarSection } from "@outpave/ds";
import { Center, Inline, Stack } from "components/layout/reusableLayouts";
import EditBusinessContact from "../EditBusinessContact";
import { FormikContact } from "hooks/useContactData";
import EditIndividualContact from "../EditIndividualContact";
import ContactType from "enums/ContactType";
import { HugeiconsIcon } from "@hugeicons/react";
import { AlertCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import ConfigureAddressDrawer from "components/common/ConfigureAddressDrawer";

type ExternalContactDetailsProps = {};

const ExternalContactDetails: React.FC<ExternalContactDetailsProps> = (
  props,
) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { logoutContact } = useContactAuth();
  const logo = useLogoForScheme();

  const [contactIdFromURL, setContactIdFromURL] = useState<string | null>(null);
  const [loadingContact, setLoadingContact] = useState<boolean>(false);
  const [openAddressDrawer, setOpenAddressDrawer] = useState<boolean>(false);
  const [contact, setContact] = useState<FormikContact | null>(null);
  const [businessContact, setBusinessContact] = useState<FormikContact | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<ContactType>(
    ContactType.Individual,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function isObjectEmpty(obj: Record<string, unknown>) {
    if (!obj) return true;
    return Object.values(obj).every((v) => v === "" || v == null);
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    if (id) setContactIdFromURL(id);
  }, [location]);

  const isIndividualTab = activeTab === ContactType.Individual;

  const fetchContact = useCallback(() => {
    setLoadingContact(true);
    setErrorMessage(null);

    contactHttpClient
      .get(`/api/contact?id=${contactIdFromURL}`)
      .then((response) => {
        setContact(response.data?.individualContact);
        setBusinessContact(response.data?.businessContact || null);
      })
      .catch((error) => {
        setErrorMessage(
          error?.response?.data?.message || "An unexpected error occurred",
        );
      })
      .finally(() => setLoadingContact(false));
  }, [contactIdFromURL]);

  useEffect(() => {
    if (contactIdFromURL) {
      fetchContact();
    }
  }, [contactIdFromURL, fetchContact]);

  const individualInitialValues = useMemo(
    () => ({
      contactType: contact?.contactType ?? null,
      firstName: contact?.firstName || "",
      lastName: contact?.lastName || "",
      email: contact?.email || "",
      phone: contact?.phone || "",
      website: contact?.website || "",
      title: contact?.title || "",
      businessName: contact?.businessName || "",
      parentContactId: contact?.parentContactId || "",
      address: {
        addressName: contact?.address?.addressName || "",
        address1: contact?.address?.address1 || "",
        address2: contact?.address?.address2 || "",
        city: contact?.address?.city || "",
        state: contact?.address?.state || "",
        zipcode: contact?.address?.zipcode || "",
        country: contact?.address?.country ?? "",
      },
      showPaymentDetails: contact?.paymentDetails?.accountNumber ? true : false,
      paymentDetails: {
        accountNumber: contact?.paymentDetails?.accountNumber || "",
        routingNumber: contact?.paymentDetails?.routingNumber || "",
        accountName: contact?.paymentDetails?.accountName || "",
      },
      paymentBankInfo: contact?.paymentBankInfo ?? "",
      removeBank: false,
    }),
    [contact],
  );

  const businessInitialValues = useMemo(
    () => ({
      contactType: businessContact?.contactType ?? null,
      firstName: businessContact?.firstName || "",
      lastName: businessContact?.lastName || "",
      email: businessContact?.email || "",
      phone: businessContact?.phone || "",
      website: businessContact?.website || "",
      title: businessContact?.title || "",
      businessName: businessContact?.businessName || "",
      address: {
        addressName: businessContact?.address?.addressName || "",
        address1: businessContact?.address?.address1 || "",
        address2: businessContact?.address?.address2 || "",
        city: businessContact?.address?.city || "",
        state: businessContact?.address?.state || "",
        zipcode: businessContact?.address?.zipcode || "",
        country: businessContact?.address?.country ?? "",
      },
      showPaymentDetails: businessContact?.paymentDetails?.accountNumber
        ? true
        : false,
      paymentDetails: {
        accountNumber: businessContact?.paymentDetails?.accountNumber || "",
        routingNumber: businessContact?.paymentDetails?.routingNumber || "",
        accountName: businessContact?.paymentDetails?.accountName || "",
      },
      paymentBankInfo: businessContact?.paymentBankInfo ?? "",
      removeBank: false,
    }),
    [businessContact],
  );

  const individualFormik = useFormik({
    enableReinitialize: true,
    initialValues: individualInitialValues,
    onSubmit: async (values: FormikContact) => {
      setLoadingContact(true);
      try {
        const commonInput: Record<string, unknown> = { ...values };

        // Only include address if it's not empty
        if (!isObjectEmpty(values.address)) {
          commonInput.address = values.address;
        } else {
          delete commonInput.address;
        }

        // Only include paymentDetails if not empty
        if (!isObjectEmpty(values.paymentDetails)) {
          commonInput.paymentDetails = values.paymentDetails;
        } else {
          delete commonInput.paymentDetails;
        }

        await contactHttpClient.put(
          `/api/contact?id=${contact?.id}`,
          commonInput,
        );
        toast.success(t("Contact updated successfully"));
        fetchContact();
      } catch (error) {
        toast.error(t("Error updating contact"));
      } finally {
        setLoadingContact(false);
      }
    },
  });

  const businessFormik = useFormik({
    enableReinitialize: true,
    initialValues: businessInitialValues,
    onSubmit: async (values: FormikContact) => {
      setLoadingContact(true);
      try {
        await contactHttpClient.put(`/api/contact?id=${businessContact?.id}`, {
          ...values,
        });
        toast.success(t("Contact updated successfully"));
        fetchContact();
      } catch (error) {
        toast.error(t("Error updating contact"));
      } finally {
        setLoadingContact(false);
      }
    },
  });

  const currentFormik = isIndividualTab ? individualFormik : businessFormik;

  const canSave =
    activeTab === ContactType.Individual
      ? individualFormik.values.firstName &&
        individualFormik.values.lastName &&
        individualFormik.values.email &&
        (!individualFormik.values.showPaymentDetails ||
          (individualFormik.values.paymentDetails.accountNumber &&
            individualFormik.values.paymentDetails.routingNumber &&
            individualFormik.values.paymentDetails.accountName))
      : businessFormik.values.businessName &&
        businessFormik.values.email &&
        businessFormik.values.phone &&
        businessFormik.values.address.address1 &&
        (!businessFormik.values.showPaymentDetails ||
          (businessFormik.values.paymentDetails.accountNumber &&
            businessFormik.values.paymentDetails.routingNumber &&
            businessFormik.values.paymentDetails.accountName));

  return (
    // handle error
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "var(--color-bg-page)",
      }}
    >
      <AppBar color="transparent">
        <AppBarSection align="start">
          <Box
            component="img"
            src={logo}
            alt="Company Logo"
            aria-label="Company Logo"
            draggable={false}
            sx={{ height: 40, width: "auto", maxWidth: 200 }}
          />
        </AppBarSection>
        <AppBarSection align="end">
          <Button variant="primary-outlined" onClick={() => logoutContact()}>
            {t("Logout")}
          </Button>
        </AppBarSection>
      </AppBar>

      <Center
        maxWidth={800}
        sx={{
          justifyContent: "center",
          flex: 1,
          paddingInline: "var(--space-4)",
          paddingBlock: "var(--space-6)",
        }}
      >
        <Stack
          gap="var(--space-6)"
          sx={{
            padding: { xs: "var(--space-4)", sm: "var(--space-6)" },
            borderRadius: "var(--radius-lg)",
            backgroundColor: "var(--color-bg-surface)",
            minHeight: { xs: "auto", sm: 560 },
          }}
        >
          {errorMessage ? (
            <Center sx={{ height: "100%" }}>
              <Stack gap="var(--space-6)" sx={{ alignItems: "center" }}>
                <HugeiconsIcon
                  icon={AlertCircleIcon}
                  size={100}
                  color="var(--color-text-danger)"
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: "var(--color-text-danger)",
                    textAlign: "center",
                  }}
                >
                  {t(errorMessage)}
                </Typography>
              </Stack>
            </Center>
          ) : (
            <>
              {businessContact ? (
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  aria-label="Contact type tabs"
                >
                  <Tab value={ContactType.Individual} label={t("My Profile")} />
                  <Tab value={ContactType.Business} label={t("My Business")} />
                </Tabs>
              ) : (
                <Typography variant="h5">{t("My Profile")}</Typography>
              )}

              {activeTab === ContactType.Individual ? (
                <EditIndividualContact
                  contactFormik={individualFormik}
                  isExternal={true}
                  setOpenAddressDrawer={setOpenAddressDrawer}
                  handleLinkToBusinessAccount={() =>
                    setActiveTab(ContactType.Business)
                  }
                  customPaymentDetailsDescription="Required to get paid via ACH / Wire"
                />
              ) : (
                <EditBusinessContact
                  contactFormik={businessFormik}
                  isExternal={true}
                  setOpenAddressDrawer={setOpenAddressDrawer}
                  customPaymentDetailsDescription="Required to get paid via ACH / Wire"
                />
              )}

              <ConfigureAddressDrawer
                open={openAddressDrawer}
                drawerTitle={[t("Contact Address")]}
                initialValues={currentFormik.values.address}
                onClose={() => setOpenAddressDrawer(false)}
                onSuccess={async (values) => {
                  currentFormik.setFieldValue("address", {
                    ...currentFormik.values.address,
                    ...values,
                  });
                  setOpenAddressDrawer(false);
                }}
              />

              <Inline
                gap="var(--space-4)"
                align="center"
                justify="between"
                sx={{ marginBlockStart: "var(--space-4)" }}
              >
                <Button
                  variant="danger-filled"
                  disabled={loadingContact || !currentFormik.dirty}
                  onClick={() => currentFormik.resetForm()}
                >
                  {t("Reset")}
                </Button>
                <Button
                  variant="primary-filled"
                  disabled={loadingContact || !currentFormik.dirty || !canSave}
                  onClick={() => currentFormik.handleSubmit()}
                >
                  {t("Save")}
                </Button>
              </Inline>
            </>
          )}
        </Stack>
      </Center>
      <LoadingWithoutModal open={loadingContact} />
    </Box>
  );
};

export default ExternalContactDetails;
